import React, { FC, useState } from "react";
import {
    Box,
    Button,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import toast from "react-hot-toast";
import axiosInstant from "../../../api";
import { CostLineItem } from "./PricingContext";
import { formatCurrency } from "../../../utils/formatCurrency";

const num = (v: string | number | null | undefined): number =>
    v == null || v === "" ? 0 : Number(v);

interface Props {
    items: CostLineItem[];
    // Exactly one of these identifies the parent for new rows.
    contentTypePriceId?: number;
    pricingPackageId?: number;
    canEdit: boolean;
    // Reload the pricing payload after any mutation.
    onChanged: () => Promise<void>;
}

const EMPTY_DRAFT = { label: "", hours: "", rate: "", cost_rate: "", cost: "", notes: "" };

const HEAD_SX = { fontWeight: 700, whiteSpace: "nowrap" } as const;
const INTERNAL_SX = { color: "var(--accent-text)", backgroundColor: "var(--tint)" } as const;

// Number inputs drop their spinner arrows: the arrows eat the right edge of a
// right-aligned value, which clipped rates like 15000.00 mid-digit.
const NUM_FIELD_SX = {
    "& input[type=number]": { MozAppearance: "textfield" },
    "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
        WebkitAppearance: "none",
        margin: 0
    }
} as const;

/**
 * Dual-rate cost sheet editor. The BILL side (rate → amount) is what clients
 * see, and the parent price (base/sell) is always the sum of the amounts.
 * The INTERNAL side (cost rate → internal cost, gold columns) tracks what
 * each line costs us; margin = bill total − internal total. The target-total
 * control re-foots the bill side — the lever for moving a derived price
 * without editing every line. Content-type sheets scale bill amounts
 * pro-rata (rates re-derive, internal stays put); package sheets grow hours
 * at fixed bill rates, with internal cost following the hours.
 */
export const LineItemsEditor: FC<Props> = ({
    items,
    contentTypePriceId,
    pricingPackageId,
    canEdit,
    onChanged
}) => {
    const [busyId, setBusyId] = useState<number | null>(null);
    const [draft, setDraft] = useState(EMPTY_DRAFT);
    const [targetTotal, setTargetTotal] = useState("");

    const hourly = items.filter((i) => i.hours != null);
    const laborHours = hourly.reduce((s, i) => s + num(i.hours), 0);
    const billTotal = items.reduce((s, i) => s + num(i.cost), 0);
    const internalTotal = items.reduce((s, i) => s + num(i.internal_cost), 0);
    const margin = billTotal - internalTotal;
    const marginPct = billTotal ? (margin / billTotal) * 100 : 0;

    const handlePatch = async (item: CostLineItem, patch: Record<string, unknown>) => {
        setBusyId(item.id);
        try {
            await axiosInstant.patch(`/pricing/cost-line-items/${item.id}`, patch);
            toast.success("Saved");
            await onChanged();
        } catch (e: any) {
            toast.error(e?.response?.data?.error ?? "Failed to save");
        } finally {
            setBusyId(null);
        }
    };

    // Numeric blur-patch for hours/rate/cost_rate — the server re-foots the
    // bill amount and internal cost from the resulting values.
    const patchNumber = (item: CostLineItem, field: "hours" | "rate" | "cost_rate", raw: string) => {
        const v = raw === "" ? null : Number(raw);
        if (v !== null && !Number.isFinite(v)) return;
        const current = item[field] == null ? null : num(item[field]);
        if (v === current) return;
        handlePatch(item, { [field]: v });
    };

    const handleDelete = async (id: number) => {
        setBusyId(id);
        try {
            await axiosInstant.delete(`/pricing/cost-line-items/${id}`);
            toast.success("Deleted");
            await onChanged();
        } catch (e: any) {
            toast.error(e?.response?.data?.error ?? "Failed to delete");
        } finally {
            setBusyId(null);
        }
    };

    const handleCreate = async () => {
        if (!draft.label.trim()) {
            toast.error("Label is required");
            return;
        }
        const hours = draft.hours === "" ? null : Number(draft.hours);
        const rate = draft.rate === "" ? null : Number(draft.rate);
        const costRate = draft.cost_rate === "" ? null : Number(draft.cost_rate);
        const cost =
            draft.cost !== ""
                ? Number(draft.cost)
                : hours != null && rate != null
                  ? hours * rate
                  : 0;
        try {
            await axiosInstant.post("/pricing/cost-line-items", {
                content_type_price_id: contentTypePriceId ?? null,
                pricing_package_id: pricingPackageId ?? null,
                label: draft.label.trim(),
                hours,
                rate,
                cost,
                cost_rate: costRate,
                internal_cost:
                    hours != null && costRate != null ? hours * costRate : null,
                notes: draft.notes.trim() || null,
                sort_order: items.length
            });
            toast.success("Line item added");
            setDraft(EMPTY_DRAFT);
            await onChanged();
        } catch (e: any) {
            toast.error(e?.response?.data?.error ?? "Failed to add line item");
        }
    };

    const handleGrossUp = async () => {
        const target = Number(targetTotal);
        if (!Number.isFinite(target) || target <= 0) {
            toast.error("Target total must be a positive number");
            return;
        }
        try {
            await axiosInstant.post("/pricing/cost-line-items/gross-up", {
                content_type_price_id: contentTypePriceId ?? null,
                pricing_package_id: pricingPackageId ?? null,
                target_total: target
            });
            toast.success(`Sheet re-footed to ${formatCurrency(target)}`);
            setTargetTotal("");
            await onChanged();
        } catch (e: any) {
            toast.error(e?.response?.data?.error ?? "Failed to re-foot sheet");
        }
    };

    return (
        <Box sx={{ py: 1 }}>
            <Table size="small" sx={{ maxWidth: 1040 }}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={HEAD_SX}>Role / Item</TableCell>
                        <TableCell sx={HEAD_SX} align="right">
                            Hours
                        </TableCell>
                        <TableCell sx={HEAD_SX} align="right">
                            Bill rate
                        </TableCell>
                        <TableCell sx={HEAD_SX} align="right">
                            Amount
                        </TableCell>
                        <TableCell sx={{ ...HEAD_SX, ...INTERNAL_SX }} align="right">
                            <Tooltip title="Internal cost rate — never shown to clients">
                                <span>Cost rate</span>
                            </Tooltip>
                        </TableCell>
                        <TableCell sx={{ ...HEAD_SX, ...INTERNAL_SX }} align="right">
                            <Tooltip title="Internal cost — never shown to clients">
                                <span>Internal</span>
                            </Tooltip>
                        </TableCell>
                        <TableCell sx={HEAD_SX}>Notes</TableCell>
                        <TableCell align="right" />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {items.map((item) => {
                        const busy = busyId === item.id;
                        const isFlat = item.hours == null;
                        return (
                            <TableRow key={item.id}>
                                <TableCell>
                                    <TextField
                                        size="small"
                                        variant="standard"
                                        defaultValue={item.label}
                                        disabled={busy || !canEdit}
                                        onBlur={(e) => {
                                            const v = e.target.value.trim();
                                            if (v && v !== item.label) handlePatch(item, { label: v });
                                        }}
                                    />
                                </TableCell>
                                <TableCell align="right" sx={{ width: 78 }}>
                                    <TextField
                                        size="small"
                                        variant="standard"
                                        type="number"
                                        inputProps={{ style: { textAlign: "right" } }}
                                        sx={NUM_FIELD_SX}
                                        defaultValue={item.hours ?? ""}
                                        placeholder="—"
                                        disabled={busy || !canEdit}
                                        onBlur={(e) => patchNumber(item, "hours", e.target.value)}
                                    />
                                </TableCell>
                                <TableCell align="right" sx={{ width: 96 }}>
                                    <TextField
                                        size="small"
                                        variant="standard"
                                        type="number"
                                        inputProps={{ style: { textAlign: "right" } }}
                                        sx={NUM_FIELD_SX}
                                        defaultValue={item.rate ?? ""}
                                        placeholder="—"
                                        disabled={busy || !canEdit}
                                        onBlur={(e) => patchNumber(item, "rate", e.target.value)}
                                    />
                                </TableCell>
                                <TableCell align="right" sx={{ width: 112 }}>
                                    {isFlat && canEdit ? (
                                        <TextField
                                            size="small"
                                            variant="standard"
                                            type="number"
                                            inputProps={{ style: { textAlign: "right" } }}
                                            sx={NUM_FIELD_SX}
                                            defaultValue={num(item.cost)}
                                            disabled={busy}
                                            onBlur={(e) => {
                                                if (e.target.value === "") {
                                                    // Cleared, not zeroed — Number("") is 0;
                                                    // restore instead of saving a $0 amount.
                                                    e.target.value = String(num(item.cost));
                                                    return;
                                                }
                                                const v = Number(e.target.value);
                                                if (Number.isFinite(v) && v !== num(item.cost)) {
                                                    handlePatch(item, { cost: v });
                                                }
                                            }}
                                        />
                                    ) : (
                                        <Typography sx={{ fontSize: 13 }}>
                                            {formatCurrency(num(item.cost))}
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell align="right" sx={{ ...INTERNAL_SX, width: 104 }}>
                                    <TextField
                                        size="small"
                                        variant="standard"
                                        type="number"
                                        inputProps={{ style: { textAlign: "right" } }}
                                        sx={NUM_FIELD_SX}
                                        defaultValue={item.cost_rate ?? ""}
                                        placeholder="—"
                                        disabled={busy || !canEdit}
                                        onBlur={(e) => patchNumber(item, "cost_rate", e.target.value)}
                                    />
                                </TableCell>
                                <TableCell align="right" sx={{ ...INTERNAL_SX, width: 116 }}>
                                    {isFlat && canEdit ? (
                                        <TextField
                                            size="small"
                                            variant="standard"
                                            type="number"
                                            inputProps={{ style: { textAlign: "right" } }}
                                            sx={NUM_FIELD_SX}
                                            defaultValue={item.internal_cost ?? ""}
                                            placeholder="—"
                                            disabled={busy}
                                            onBlur={(e) => {
                                                const v =
                                                    e.target.value === ""
                                                        ? null
                                                        : Number(e.target.value);
                                                if (v === null || Number.isFinite(v)) {
                                                    if (v !== (item.internal_cost == null ? null : num(item.internal_cost))) {
                                                        handlePatch(item, { internal_cost: v });
                                                    }
                                                }
                                            }}
                                        />
                                    ) : (
                                        <Typography sx={{ fontSize: 13 }}>
                                            {item.internal_cost == null
                                                ? "—"
                                                : formatCurrency(num(item.internal_cost))}
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        size="small"
                                        variant="standard"
                                        defaultValue={item.notes ?? ""}
                                        placeholder="—"
                                        disabled={busy || !canEdit}
                                        onBlur={(e) => {
                                            const v = e.target.value.trim();
                                            if (v !== (item.notes ?? "")) {
                                                handlePatch(item, { notes: v || null });
                                            }
                                        }}
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    {canEdit && (
                                        <IconButton
                                            size="small"
                                            onClick={() => handleDelete(item.id)}
                                            disabled={busy}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                    {canEdit && (
                        <TableRow>
                            <TableCell>
                                <TextField
                                    size="small"
                                    variant="standard"
                                    placeholder="Editor"
                                    value={draft.label}
                                    onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                                />
                            </TableCell>
                            <TableCell align="right">
                                <TextField
                                    size="small"
                                    variant="standard"
                                    type="number"
                                    inputProps={{ style: { textAlign: "right" } }}
                                    sx={NUM_FIELD_SX}
                                    placeholder="—"
                                    value={draft.hours}
                                    onChange={(e) => setDraft({ ...draft, hours: e.target.value })}
                                />
                            </TableCell>
                            <TableCell align="right">
                                <TextField
                                    size="small"
                                    variant="standard"
                                    type="number"
                                    inputProps={{ style: { textAlign: "right" } }}
                                    sx={NUM_FIELD_SX}
                                    placeholder="—"
                                    value={draft.rate}
                                    onChange={(e) => setDraft({ ...draft, rate: e.target.value })}
                                />
                            </TableCell>
                            <TableCell align="right">
                                <TextField
                                    size="small"
                                    variant="standard"
                                    type="number"
                                    inputProps={{ style: { textAlign: "right" } }}
                                    sx={NUM_FIELD_SX}
                                    placeholder="auto"
                                    value={draft.cost}
                                    onChange={(e) => setDraft({ ...draft, cost: e.target.value })}
                                />
                            </TableCell>
                            <TableCell align="right" sx={INTERNAL_SX}>
                                <TextField
                                    size="small"
                                    variant="standard"
                                    type="number"
                                    inputProps={{ style: { textAlign: "right" } }}
                                    sx={NUM_FIELD_SX}
                                    placeholder="—"
                                    value={draft.cost_rate}
                                    onChange={(e) => setDraft({ ...draft, cost_rate: e.target.value })}
                                />
                            </TableCell>
                            <TableCell align="right" sx={INTERNAL_SX}>
                                <Typography sx={{ fontSize: 12, color: "var(--accent-text)" }}>auto</Typography>
                            </TableCell>
                            <TableCell>
                                <TextField
                                    size="small"
                                    variant="standard"
                                    placeholder="Notes"
                                    value={draft.notes}
                                    onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                                />
                            </TableCell>
                            <TableCell align="right">
                                <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={handleCreate}
                                    disabled={!draft.label.trim()}
                                >
                                    Add
                                </Button>
                            </TableCell>
                        </TableRow>
                    )}
                    <TableRow>
                        <TableCell sx={{ fontWeight: 700, fontSize: 13 }}>
                            Sheet total (= price)
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 13 }} align="right">
                            {laborHours || ""}
                        </TableCell>
                        <TableCell />
                        <TableCell sx={{ fontWeight: 700, fontSize: 13 }} align="right">
                            {formatCurrency(billTotal)}
                        </TableCell>
                        <TableCell sx={{ ...INTERNAL_SX, fontWeight: 700, fontSize: 13 }} align="right" colSpan={2}>
                            {formatCurrency(internalTotal)}
                        </TableCell>
                        <TableCell
                            colSpan={2}
                            sx={{
                                fontWeight: 700,
                                fontSize: 13,
                                color: margin < 0 ? "var(--down)" : "var(--up)"
                            }}
                        >
                            Margin {formatCurrency(margin)} ({marginPct.toFixed(1)}%)
                            {margin < 0 ? " — below cost" : ""}
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
            {canEdit && (
                <Box className="flex items-center gap-2" sx={{ mt: 1.5 }}>
                    <TextField
                        size="small"
                        type="number"
                        label="Target total"
                        value={targetTotal}
                        onChange={(e) => setTargetTotal(e.target.value)}
                        sx={{ width: 160 }}
                    />
                    <Button size="small" variant="outlined" onClick={handleGrossUp} disabled={!targetTotal}>
                        Re-foot sheet
                    </Button>
                    <Typography sx={{ fontSize: 12, color: "var(--text-2)" }}>
                        {pricingPackageId
                            ? "Grows hours at fixed bill rates so the sheet — and the price — hits the target. Internal costs follow the added hours; flat rows stay put."
                            : "Scales every bill amount pro-rata so the sheet — and the price — hits the target. Internal costs stay put."}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default LineItemsEditor;
